import React from 'react';
import { Box, Chip, Divider, Paper, Skeleton, Stack, Tooltip, Typography } from '@mui/material';
import { alpha } from '@mui/material/styles';
import { Icon } from '@iconify/react/dist/iconify.js';

//INDIAN GROUPING , NO CURRENCY SYMBOL ON PURPOSE ( SEE THE MIXED CURRENCY NOTE BELOW )
const formatAmount = (value) =>
  new Intl.NumberFormat('en-IN', { maximumFractionDigits: 2 }).format(Number(value) || 0);

/**
 * one stat tile : colored mark carries identity , the value stays in ink
 * @author Akhil E M
 * @createdAt 04-08-2026
 * @updates 04-08-2026(Akhil EM),
 * @type component
 */
const StatTile = ({ icon, label, value, color, loading }) => (
  <Stack spacing={1.25} sx={{ flex: 1, minWidth: 0, px: { xs: 0, md: 1 } }}>
    <Stack direction="row" alignItems="center" spacing={1}>
      <Box
        sx={{
          width: 32,
          height: 32,
          borderRadius: 1.5,
          display: 'grid',
          placeItems: 'center',
          flexShrink: 0,
          color: `${color}.main`,
          bgcolor: (theme) => alpha(theme.palette[color].main, 0.12),
        }}
      >
        <Icon icon={icon} width="18" height="18" />
      </Box>
      <Typography variant="body2" sx={{ color: 'text.secondary', fontWeight: 500 }} noWrap>
        {label}
      </Typography>
    </Stack>

    {loading ? (
      <Skeleton variant="text" width="70%" height={40} />
    ) : (
      <Tooltip title={formatAmount(value)} placement="top">
        <Typography
          variant="h4"
          sx={{
            fontWeight: 600,
            color: 'text.primary',
            lineHeight: 1.2,
            //PROPORTIONAL FIGURES , TABULAR ONLY BELONGS IN COLUMNS OF NUMBERS
            fontVariantNumeric: 'proportional-nums',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
          }}
          noWrap
        >
          {formatAmount(value)}
        </Typography>
      </Tooltip>
    )}
  </Stack>
);

/**
 * skillx finance summary : a kpi row of three stat tiles plus a collection meter.
 * totals come from the api and cover every filtered record , not just the visible page.
 * @author Akhil E M
 * @createdAt 04-08-2026
 * @updates 04-08-2026(Akhil EM),
 * @type component
 * @packages @mui/material,@iconify/react
 */
const SkillXFinanceSummary = ({ summary, loading = false }) => {
  const totalSales = Number(summary?.totalSales || 0);
  const totalCollected = Number(summary?.totalCollected || 0);
  const pendingAmount = Number(summary?.pendingAmount ?? totalSales - totalCollected);
  const recordCount = Number(summary?.recordCount || 0);

  const collectedPercent = totalSales > 0 ? Math.min(100, Math.max(0, (totalCollected / totalSales) * 100)) : 0;

  //THE METER FILL CARRIES THE SEVERITY , THE PERCENTAGE TEXT REPEATS IT SO COLOR IS NEVER THE ONLY CHANNEL
  const meterColor = collectedPercent >= 75 ? 'success' : collectedPercent >= 40 ? 'warning' : 'error';

  return (
    <Paper
      elevation={0}
      sx={{
        p: { xs: 2, md: 2.5 },
        mb: 2,
        borderRadius: 3,
        border: '1px solid',
        borderColor: 'divider',
        background: (theme) =>
          `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.04)} 0%, ${alpha(
            theme.palette.background.paper,
            0,
          )} 55%)`,
      }}
    >
      <Stack
        direction="row"
        alignItems="center"
        justifyContent="space-between"
        spacing={1}
        sx={{ mb: 2 }}
      >
        <Typography variant="subtitle2" sx={{ color: 'text.secondary', letterSpacing: 0.3 }}>
          SkillX finance summary
        </Typography>
        {loading ? (
          <Skeleton variant="rounded" width={96} height={24} />
        ) : (
          <Chip
            size="small"
            variant="outlined"
            label={`${new Intl.NumberFormat('en-IN').format(recordCount)} records`}
          />
        )}
      </Stack>

      <Stack
        direction={{ xs: 'column', md: 'row' }}
        spacing={{ xs: 2.5, md: 0 }}
        divider={<Divider orientation="vertical" flexItem sx={{ display: { xs: 'none', md: 'block' } }} />}
      >
        <StatTile
          icon="mdi:tag-outline"
          label="Total sale amount"
          value={totalSales}
          color="info"
          loading={loading}
        />
        <StatTile
          icon="mdi:cash-check"
          label="Total fee collected"
          value={totalCollected}
          color="success"
          loading={loading}
        />
        <StatTile
          icon="mdi:clock-alert-outline"
          label="Pending amount"
          value={pendingAmount}
          color="warning"
          loading={loading}
        />
      </Stack>

      {/* METER : A SINGLE RATIO AGAINST A LIMIT . TRACK IS A LIGHTER STEP OF THE FILL RAMP */}
      <Box sx={{ mt: 2.5 }}>
        <Stack direction="row" alignItems="baseline" justifyContent="space-between" sx={{ mb: 0.75 }}>
          <Typography variant="caption" sx={{ color: 'text.secondary' }}>
            Collected against sale value
          </Typography>
          <Typography variant="caption" sx={{ color: 'text.primary', fontWeight: 600 }}>
            {loading ? '—' : `${collectedPercent.toFixed(1)}%`}
          </Typography>
        </Stack>
        <Box
          sx={{
            height: 8,
            borderRadius: 999,
            overflow: 'hidden',
            bgcolor: (theme) => alpha(theme.palette[meterColor].main, 0.16),
          }}
        >
          <Box
            sx={{
              height: '100%',
              width: `${loading ? 0 : collectedPercent}%`,
              borderRadius: 999,
              bgcolor: `${meterColor}.main`,
              transition: 'width 400ms ease',
            }}
          />
        </Box>
      </Box>
    </Paper>
  );
};

export default SkillXFinanceSummary;
